/**
*****************************************
*                                       *
* NAGARRO  - CSX - CONFIDENTIAL         *
*                                       *
*****************************************
*/

import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TabSearchParameter } from '../../../core/model/tab-search-parameter.model';
import { TrainData } from '../../../core/model/train-data.model';
import { AppUtilityService } from '../../../core/services/app-utility.service';
import { ConfigService } from '../../../core/services/config.service';
import { LmsModuleService } from '../../../core/services/lms-module.service';
import { PopupService } from '../../../core/services/popup.service';
import { StaticDataStoreService } from '../../../core/services/static-datastore.service';
import { UserService } from '../../../core/services/user.service';
import { AddToPendingList } from '../../../terminal/shared/model/terminal-pending.model';


/**
 * @export
 * @class LmsTrainContextMenuComponent
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'lms-train-context-menu',
  templateUrl: './lms-train-context-menu.component.html',
  styleUrls: ['./lms-train-context-menu.component.scss']
})
export class LmsTrainContextMenuComponent implements OnChanges, OnDestroy {

  /**
  * This field is used to store the details of Locomotive
  * for which we are going to display the details in context panel
  * @type {TrainData}
  * @memberOf LmsTrainContextMenuComponent
  */
  @Input() train: TrainData;

  /**
   * This field is used to store the id of svg html element
   * for displaying the context panel with respect to this element
   * @type {*}
   * @memberOf LmsTrainContextMenuComponent
   */
  @Input() trainId: any;

  @Input() currentTerminal: string;

  @Input() windowCode: string;
  @Input() screenName: string;
  @Input() addToPendingList: Array<AddToPendingList>;

  cancelPlanEnable: boolean;

  rightPanlePosition: string;

  /**
   * this field is used to get the labels from configService
   * @type {*}
   * @memberOf LmsTrainContextMenuComponent
   */
  trainLabel: any;

  /**
   * this field is used to give position from left to left side panel of context panel
   * @type {string}
   * @memberOf LmsTrainContextMenuComponent
   */
  panelLeftPosX: string;

  /**
   * this field is used to give position from top to left side panel of context panel
   * @type {string}
   * @memberOf LmsTrainContextMenuComponent
   */
  panelLeftPosY: string;

  /**
   * this field is used to give position from left to right side panel of context panel
   * @type {string}
   * @memberOf LmsTrainContextMenuComponent
   */
  panelRightPosX: string;

  /**
   * this field is used to give position from top to right side panel of context panel
   * @type {string}
   * @memberOf LmsTrainContextMenuComponent
   */
  panelRightPosY: string;

  /**
   * this field is used to store the height of window on which application is running.
   * @type {number}
   * @memberOf LmsTrainContextMenuComponent
   */
  windowHeight: number;

  /**
   * this field is used to store the width of window on which application is running.
   * @type {number}
   * @memberOf LmsTrainContextMenuComponent
   */
  windowWidth: number;

  /**
   * this field is used to store the scroll hight of page
   * @type {number}
   * @memberOf LmsTrainContextMenuComponent
   */
  scrollHeight: number;

  /**
   * this field is used to provide dynamic width to
   * panel containing links to data, profile, history etc.
   * @type {string}
   * @memberOf LmsTrainContextMenuComponent
   */
  leftWidth: string;

  /**
   * this field is used to provide dynamic height to
   * panel containing links to data, profile, history etc.
   * @type {string}
   * @memberOf LmsTrainContextMenuComponent
   */
  leftHeight: string;

  /**
   * this fields states the hight of left side panel
   * that contains the menu items like data, history etc.
   * @memberOf LmsTrainContextMenuComponent
   */
  leftMenuHeight = 137;

  /**
   * this fields states the width of left side panel
   * that contains the menu items like data, history etc.
   * @memberOf LmsTrainContextMenuComponent
   */
  leftMenuWidth = 107;

  /**
   * this fields states the hight of right side panel
   * that contains the details of a locomotive.
   * @memberOf LmsTrainContextMenuComponent
   */
  readonly rightPanelHeight = 430;

  /**
   * this fields states the width of right side panel
   * that contains the details of a locomotive.
   * @memberOf LmsTrainContextMenuComponent
   */
  readonly rightPanelWidth = 375;

  /**
   * this field states the width of left side navigation panel
   * which is used when we position the context panel
   * @memberOf LmsTrainContextMenuComponent
   */
  readonly leftNavWidth = 50;

  /**
   * this field states the height of top header panel
   * which is used when we position the context panel
   * @memberOf LmsTrainContextMenuComponent
   */
  readonly topHeaderHeight = 70;

  /**
   * Creates an instance of LmsTrainContextMenuComponent.
   * and inject the services like configService and lmsModuleService
   * @param {ConfigService} configService
   * @param {LmsModuleService} lmsModuleService
   * @memberOf LmsTrainContextMenuComponent
   */
  showHeader: boolean;

  constructor(private configService: ConfigService,
    private lmsModuleService: LmsModuleService,
    private router: Router,
    private popupService: PopupService,
    private staticDataStoreService: StaticDataStoreService,
    private appUtilService: AppUtilityService, 
    public user: UserService) {
    this.trainLabel = this.configService.getConfigData().trainLabel;

     if(staticDataStoreService.getValue('SHOW_HEADER') == false)
        this.showHeader = false;
    else
        this.showHeader = true;
  }

  /**
   * this method is used to set the scroll position back to top
   * this case occured when user switch between context panel
   * of multiple trains
   * @private
   * @memberOf LmsTrainContextMenuComponent
   */
  private changeScrollPosition() {
    const rightPanel = document.getElementById('contextPanelID');
    if (rightPanel) {
      rightPanel.scrollTop = 0;
    }
  }

  /**
   * When any changes occured in above data of this Train
   * this method is basically used to position the train
   * context menu with respect to a train on which right click occurs
   * @memberOf LmsTrainContextMenuComponent
   */
  ngOnChanges() {
    this.setDefaultValues();
    this.windowHeight = document.documentElement.clientHeight;
    this.windowWidth = document.documentElement.clientWidth;
    this.scrollHeight = document.body.scrollTop;
    const mySvg = document.getElementById(this.trainId);
    const rect = mySvg.getBoundingClientRect();
    let leftPanel = document.getElementById('locomotiveBriefLeftMenu');
    if (leftPanel) {
      const leftElem = leftPanel.getBoundingClientRect();
      this.leftMenuHeight = leftElem.height;
      this.leftMenuWidth = leftElem.width;
    }

    if (rect.width !== this.leftMenuWidth) {
      this.leftMenuWidth = rect.width;
      this.leftWidth = rect.width + 'px';
    } else {
      this.leftWidth = this.leftMenuWidth + 'px';
      this.leftHeight = this.leftMenuHeight + 'px';
    }

    const elementTop = rect.top;
    const elementLeft = rect.left;

    const lowerAvailable = this.windowHeight - elementTop - rect.height - 18;
    const upperAvailable = elementTop;
    const leftAvailable = elementLeft;
    const rightAvailable = this.windowWidth - elementLeft - rect.width;

    setTimeout(() => {
      leftPanel = document.getElementById('trainBriefLeftMenu');
      const oldvalue = this.leftMenuHeight;
      this.leftMenuHeight = leftPanel.scrollHeight;
      this.leftHeight = this.leftMenuHeight + 'px';
      const diff = Math.abs(oldvalue - this.leftMenuHeight);
      if (lowerAvailable > this.rightPanelHeight) {
        this.panelLeftPosY = this.scrollHeight + elementTop + rect.height - this.topHeaderHeight + 'px';
      } else {
        if (!(lowerAvailable > upperAvailable)) {
          if (this.rightPanlePosition && this.rightPanlePosition !== 'top') {
            this.panelLeftPosY = this.scrollHeight + elementTop + rect.height - this.topHeaderHeight + 'px';
          } else {
            this.panelLeftPosY = this.scrollHeight + elementTop - this.leftMenuHeight - this.topHeaderHeight + 'px';
          }
        } else {
          this.panelLeftPosY = this.scrollHeight + elementTop + rect.height - this.topHeaderHeight + 'px';
        }
      }
    }, 5);

    if (lowerAvailable > this.rightPanelHeight) {
      this.panelLeftPosX = elementLeft - this.leftNavWidth + 'px';
      this.panelLeftPosY = this.scrollHeight + elementTop + rect.height - this.topHeaderHeight + 'px';
      if (rightAvailable > this.rightPanelWidth) {
        this.panelRightPosX = elementLeft + rect.width - this.leftNavWidth + 'px';
        this.panelRightPosY = this.scrollHeight + elementTop - this.topHeaderHeight + 'px';
      } else {
        if (leftAvailable > this.rightPanelWidth) {
          this.panelRightPosY = this.scrollHeight + elementTop - this.topHeaderHeight + 'px';
          this.panelRightPosX = elementLeft - this.rightPanelWidth - this.leftNavWidth + 'px';
        } else {
          this.panelRightPosY = this.scrollHeight + elementTop - this.topHeaderHeight + 'px';
          this.panelRightPosX = elementLeft + rect.width - this.leftNavWidth + 'px';
        }
      }
      this.rightPanlePosition = 'down';
    } else {
      if (upperAvailable > this.rightPanelHeight) {
        this.panelLeftPosX = elementLeft - this.leftNavWidth + 'px';
        this.panelLeftPosY = this.scrollHeight + elementTop - this.topHeaderHeight - this.leftMenuHeight + 'px';
        if (rightAvailable > this.rightPanelWidth) {
          this.panelRightPosX = elementLeft + rect.width - this.leftNavWidth + 'px';
          this.panelRightPosY = this.scrollHeight + elementTop - this.rightPanelHeight - this.topHeaderHeight + rect.height - 12 + 'px';
        } else {
          if (leftAvailable > this.rightPanelWidth) {
            this.panelRightPosY = this.scrollHeight + elementTop - this.rightPanelHeight + rect.height - this.topHeaderHeight - 12 + 'px';
            this.panelRightPosX = elementLeft - this.leftNavWidth - this.rightPanelWidth + 'px';
          } else {
            this.panelRightPosX = elementLeft - this.leftNavWidth + this.leftMenuWidth + 'px';
            this.panelRightPosY = this.scrollHeight + - this.rightPanelHeight + 'px';
          }
        }
        this.rightPanlePosition = 'top';
      } else {
        this.rightPanlePosition = 'any';
        this.panelLeftPosX = elementLeft - this.leftNavWidth + 'px';
        this.panelLeftPosY = this.scrollHeight + elementTop - this.topHeaderHeight + rect.height + 'px';
        if (rightAvailable > this.rightPanelWidth) {
          this.panelRightPosX = elementLeft - this.leftNavWidth + rect.width + 'px';
          this.panelRightPosY = this.scrollHeight + elementTop - this.topHeaderHeight + 'px';
        } else {
          if (leftAvailable > this.rightPanelWidth) {
            this.panelRightPosY = this.scrollHeight + elementTop - this.topHeaderHeight + 'px';
            this.panelRightPosX = elementLeft - this.leftNavWidth - this.rightPanelWidth + 'px';
          } else {
            this.panelRightPosY = this.scrollHeight + elementTop - this.topHeaderHeight + 'px';
            this.panelRightPosX = elementLeft - this.leftNavWidth + rect.width + 'px';
          }
        }
      }
    }
    this.changeScrollPosition();
    this.cancelPlanEnable = this.checkForCancelPlanEnableDiable();
  }

  /**
   * @private
   * @returns {boolean}
   *
   * @memberOf LmsTrainContextMenuComponent
   */
  private checkForCancelPlanEnableDiable(): boolean {
    let returnValue = false;
    let counter = 0;
    if (this.train && this.train.locoDetails && this.train.locoDetails.length > 0) {
      this.train.locoDetails.forEach((locomotive) => {
        if (locomotive && locomotive.locomotiveIdentifier && locomotive.locomotiveIdentifier.toUpperCase() !== 'ZZZZZZZZZZZZZZ'
          && locomotive.assignedPlannedFlag && locomotive.assignedPlannedFlag.trim().toUpperCase() === 'P' && locomotive.plannedOrCurrentAssign && locomotive.plannedOrCurrentAssign.length > 0) {
          counter++;
        }
      });
      if (counter > 0) {
        returnValue = true;
      }
    }
    return returnValue;
  }

  /**
   * this method is used to navigate the user to train screen
   * depends on user's click whether it is Data or Profile or Notes
   * @param {string} subType
   * @param {string} type
   * @memberOf LmsTrainContextMenuComponent
   */
  public showTrainTabData(subType: string, type: string) {
    const tabSearchParameter = <TabSearchParameter>({ searchText: this.train.trainIdentifier });
    //open brand window with locomotive
    window.open(
      window.location.href.split('?')[0] + '?type=train&subtype=' + subType.toLowerCase() + '&search=' + tabSearchParameter.searchText,
        '_blank', "scrollbars=yes,resizable=yes,top=100,left=500,width=1000,height=800"
      );
  }

  /**
   * this method is used to set the values to default
   * so that we can recalculate for every context panel
   * @private
   * @memberOf LmsTrainContextMenuComponent
   */
  private setDefaultValues() {
    this.leftWidth = '';
    this.leftHeight = '';
    this.leftMenuHeight = 155;
    this.leftMenuWidth = 107;
  }

  /**
   * @memberOf LmsTrainContextMenuComponent
   */
  ngOnDestroy() {
  }

  /**
   * This method returns dash only when all the values given are null or blank
   * @param {string[]} value
   * @returns
   * @memberOf LmsTrainContextMenuComponent
   */
  returnDash(value: string[]) {
    return this.appUtilService.checkMultipleBlankStatus(value);
  }

  /**
   * @memberOf LmsTrainContextMenuComponent
   */
  cancelAllPlannedLocomotive() {
    if (this.train) {
      const request = {
        thpTerminal: '',
        terminalHandlingType: '',
        trainId: this.train.trainIdentifier,
        otherLocosList: this.getLocomotivesForCancelPlan()
      };

      const operation = {
        operation: 'CancelTrainLocomotivePlan',
        data: request,
        currentTerminal: this.currentTerminal,
        windowCode: this.windowCode,
        mainLocodata: this.getLocomotivesDetailsForCancelPlan(),
        addToPendingList: this.addToPendingList,
        trainData: [this.train],
        screenName: this.screenName || ''

      };
      this.popupService.setPopupOperation(operation);
    }
  }

  /**
   * @returns {*}
   *
   * @memberOf LmsTrainContextMenuComponent
   */
  getLocomotivesForCancelPlan(): any {
    const locos = [];
    if (this.train.locoDetails && this.train.locoDetails.length > 0) {
      this.train.locoDetails.forEach((locomotive) => {
        if (locomotive && locomotive.locomotiveIdentifier && locomotive.locomotiveIdentifier.toUpperCase() !== 'ZZZZZZZZZZZZZZ'
          && locomotive.assignedPlannedFlag && locomotive.assignedPlannedFlag.trim().toUpperCase() === 'P' && locomotive.plannedOrCurrentAssign && locomotive.plannedOrCurrentAssign.length > 0) {
          const select = {
            rowID: '',
            locomotiveID: this.formatLocomotiveIdForRequest(locomotive.locomotiveIdentifier),
            assignedPlannedFlag: locomotive.assignedPlannedFlag,
            qualifier: locomotive.qualifier
          };
          locos.push(select);
        }
      });
    }
    return locos;
  }


  /**
   * @returns {*}
   *
   * @memberOf LmsTrainContextMenuComponent
   */
  getLocomotivesDetailsForCancelPlan(): any {
    const locos = [];

    if (this.train.locoDetails && this.train.locoDetails.length > 0) {
      this.train.locoDetails.forEach((locomotive) => {
        if (locomotive && locomotive.locomotiveIdentifier && locomotive.locomotiveIdentifier.toUpperCase() !== 'ZZZZZZZZZZZZZZ'
          && this.appUtilService.isPlannedLoco(locomotive)) {
          locos.push(locomotive);
        }
      });
    }
    return locos;
  }

  /**
   * @private
   * @param {string} value
   * @returns {string}
   *
   * @memberOf LmsTrainContextMenuComponent
   */
  private formatLocomotiveIdForRequest(value: string): string {
    let formattedValue = value;
    if (value) {
      value = value.trim();
      if (this.isNumeric(value.charAt(0))) {
      } else {
        const v = value.match(/\d+/);
        if (v) {
          const locoInitial = (v.index <= 4) ? value.substring(0, v.index).trim() : value.substring(0, 4).trim();
          const locoNumber = (v.index <= 4) ?
            value.substring(v.index, value.length).trim() : value.substring(4, value.length).trim();

          if (this.isNumeric(locoNumber)) {
            formattedValue = this.rightPad(locoInitial, ' ', 4) + '    ' + this.leftPad(locoNumber, '0', 6);
          }
        }
      }
    }
    return formattedValue;
  }

  /**
   * @param {string} value
   * @returns {boolean}
   *
   * @memberOf LmsTrainContextMenuComponent
   */
  public isNumeric(value: string): boolean {
    const v = value.match(/^\d+$/);
    if (v) {
      if (v.toString() === value) {
        return true;
      }
    }
    return false;
  }

  /**
   * @param {any} str
   * @param {any} padString
   * @param {any} max
   * @returns {string}
   *
   * @memberOf LmsTrainContextMenuComponent
   */
  leftPad(str, padString, max): string {
    str = str.toString();
    return str.length < max ? this.leftPad(padString + str, padString, max) : str;
  }

  /**
   * @private
   * @param {any} str
   * @param {any} padString
   * @param {any} max
   * @returns {string}
   *
   * @memberOf LmsTrainContextMenuComponent
   */
  private rightPad(str, padString, max): string {
    str = str.toString();
    return str.length < max ? this.rightPad(str + padString, padString, max) : str;
  }
}
